import { User, UserRole } from '../types/user'
import { formatUserName } from '../utils/name'
import {
  FiEdit2,
  FiUserCheck,
  FiUserX,
  FiKey,
  FiShield,
  FiShieldOff,
  FiTrash2,
} from 'react-icons/fi'
import './UserList.css'

interface UserListProps {
  users: User[]
  onEdit: (user: User) => void
  onDelete: (id: string, name: string) => void
  onActivate: (id: string) => void
  onDeactivate: (id: string) => void
  onResetPassword: (id: string) => void
  onEnable2fa: (id: string) => void
  onDisable2fa: (id: string) => void
}

export function UserList({
  users,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  onResetPassword,
  onEnable2fa,
  onDisable2fa,
}: UserListProps) {
  const getRoleBadgeClass = (role: UserRole) => {
    switch (role) {
      case 'admin':
        return 'badge-admin'
      case 'doctor':
        return 'badge-doctor'
      case 'nurse':
        return 'badge-nurse'
      case 'receptionist':
        return 'badge-receptionist'
      default:
        return ''
    }
  }

  if (users.length === 0) {
    return (
      <div className="empty-state">
        <p>No users found</p>
      </div>
    )
  }

  return (
    <div className="user-list">
      <table className="user-table" role="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Username</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>2FA</th>
            <th>Last Login</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>
                <strong>
                  {formatUserName(user)}
                </strong>
              </td>
              <td>{user.username}</td>
              <td>{user.email}</td>
              <td>
                <span className={`badge ${getRoleBadgeClass(user.role)}`}>{user.role}</span>
              </td>
              <td>
                {user.isActive ? (
                  <span className="badge badge-active">Active</span>
                ) : (
                  <span className="badge badge-inactive">Inactive</span>
                )}
              </td>
              <td>
                {user.twoFactorEnabled ? (
                  <span className="badge badge-2fa-enabled">Enabled</span>
                ) : (
                  <span className="badge badge-2fa-disabled">Disabled</span>
                )}
              </td>
              <td>
                {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
              </td>
              <td>
                <div className="action-buttons">
                  <button
                    className="btn-link icon-button"
                    onClick={() => onEdit(user)}
                    aria-label={`Edit ${formatUserName(user)}`}
                    title="Edit user"
                  >
                    <FiEdit2 aria-hidden="true" />
                  </button>
                  {user.isActive ? (
                    <button
                      className="btn-link icon-button"
                      onClick={() => onDeactivate(user.id)}
                      aria-label={`Deactivate ${formatUserName(user)}`}
                      title="Deactivate user"
                    >
                      <FiUserX aria-hidden="true" />
                    </button>
                  ) : (
                    <button
                      className="btn-link icon-button"
                      onClick={() => onActivate(user.id)}
                      aria-label={`Activate ${formatUserName(user)}`}
                      title="Activate user"
                    >
                      <FiUserCheck aria-hidden="true" />
                    </button>
                  )}
                  <button
                    className="btn-link icon-button"
                    onClick={() => onResetPassword(user.id)}
                    aria-label={`Reset password for ${formatUserName(user)}`}
                    title="Reset password"
                  >
                    <FiKey aria-hidden="true" />
                  </button>
                  {user.twoFactorEnabled ? (
                    <button
                      className="btn-link icon-button"
                      onClick={() => onDisable2fa(user.id)}
                      aria-label={`Disable 2FA for ${formatUserName(user)}`}
                      title="Disable 2FA"
                    >
                      <FiShieldOff aria-hidden="true" />
                    </button>
                  ) : (
                    <button
                      className="btn-link icon-button"
                      onClick={() => onEnable2fa(user.id)}
                      aria-label={`Enable 2FA for ${formatUserName(user)}`}
                      title="Enable 2FA"
                    >
                      <FiShield aria-hidden="true" />
                    </button>
                  )}
                  <button
                    className="btn-link btn-danger icon-button"
                    onClick={() => onDelete(user.id, formatUserName(user))}
                    aria-label={`Delete ${formatUserName(user)}`}
                    title="Delete user"
                  >
                    <FiTrash2 aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}














